// Program content for the hackathon page. Agenda, tracks, judges, speakers, and
// sponsor perks come from the event guidebook; everything else is sourced from
// event.ts and the Terac x Berkeley challenge write-up.

export interface AgendaEntry {
  /** stable list key: the guidebook's blocks can share a start time */
  id: string;
  time: string;
  title: string;
  note?: string;
}

export const AGENDA: AgendaEntry[] = [
  {
    id: "doors",
    time: "08:30",
    title: "Doors open",
    note: "Breakfast and check-in",
  },
  { id: "welcome", time: "09:00", title: "Welcome" },
  {
    id: "opening",
    time: "09:15",
    title: "Opening ceremony",
    note: "Rules, judging criteria, and track breakdowns. The doc with API keys and credits goes out here.",
  },
  {
    id: "sponsor-stage",
    time: "09:45",
    title: "Sponsor stage time",
    note: "Replay, Linq, Fastino Labs, and Superserve, five minutes each. Attendance is required.",
  },
  {
    id: "sponsor-booths",
    time: "10:20",
    title: "Sponsor Q&A at the booths",
    note: "Bring your credit and API questions.",
  },
  {
    id: "hacking",
    time: "10:45",
    title: "Hacking begins",
    note: "Eight hours straight. Lunch and dinner flow through with no scheduled stop, and mentors circulate all day.",
  },
  { id: "lock", time: "18:45", title: "Submissions lock" },
  { id: "judging", time: "19:00", title: "Judging" },
  { id: "panel", time: "19:15", title: "Speaker panel" },
  {
    id: "finalize",
    time: "20:00",
    title: "Judges finalize",
    note: "Winners compiled",
  },
  {
    id: "closing",
    time: "20:20",
    title: "Closing ceremony",
    note: "Winners announced",
  },
  { id: "wrap", time: "20:50", title: "Wrap-up" },
];

export interface Step {
  index: string;
  title: string;
  body: string;
}

export const STEPS: Step[] = [
  {
    index: "01",
    title: "Build something people can respond to",
    body: "A simple app where a person uses your product, reacts to it, or labels, rates, ranks, or compares what your system produces.",
  },
  {
    index: "02",
    title: "Call the Terac API to bring the people",
    body: "Launch your task on Terac and real people complete it within hours, so the full loop fits inside the day. You focus on building; recruiting and incentives are on us.",
  },
  {
    index: "03",
    title: "Turn that input into a better project",
    body: "Product changes, prompt and retrieval changes, evals, or fine-tuning. Then show a clear before and after, ideally judged by a fresh round of humans.",
  },
];

export interface Track {
  /** sponsor whose track this is, or "Main stage" for the overall prizes */
  kicker: string;
  title: string;
  /** ordered placings; an empty list renders as "prizes announced on the day" */
  prizes: string[];
  criteria?: string;
  /** background logo, drawn at low opacity behind the card */
  logo?: string;
  logoWidth?: number;
  logoHeight?: number;
  /** artwork is light-on-dark, so flip it to read on the cream card */
  logoInvert?: boolean;
}

export const TRACKS: Track[] = [
  {
    kicker: "Main stage",
    title: "Best Overall Project",
    prizes: ["$2,500"],
    criteria: "The best overall project, decided by the main panel of judges.",
    logo: "/brand/logo.svg",
    logoWidth: 139,
    logoHeight: 146,
  },
  {
    kicker: "Main stage",
    title: "Best Overall Agent-Run Company",
    prizes: ["$2,500"],
    criteria:
      "The agent-run company that earns the most revenue during the hackathon. Revenue is the only criterion.",
    logo: "/brand/logo.svg",
    logoWidth: 139,
    logoHeight: 146,
  },
  {
    kicker: "Terac",
    title: "Best Use of Terac",
    prizes: [],
    logo: "/brand/logo.svg",
    logoWidth: 139,
    logoHeight: 146,
  },
  {
    kicker: "Linq",
    title: "Best Use of Linq",
    prizes: ["$1,500", "$1,000"],
    logo: "/hackathon/sponsors/linq.png",
    logoWidth: 1200,
    logoHeight: 340,
    logoInvert: true,
  },
  {
    kicker: "Replay",
    title: "Best Use of Replay",
    prizes: ["$1,000", "$500"],
    logo: "/hackathon/sponsors/replay.svg",
    logoWidth: 34,
    logoHeight: 40,
  },
  {
    kicker: "Superserve",
    title: "Best Use of Superserve",
    prizes: ["$1,000", "$500"],
    logo: "/hackathon/sponsors/superserve.svg",
    logoWidth: 705,
    logoHeight: 130,
  },
  {
    kicker: "Fastino Labs",
    title: "Best Use of Pioneer",
    prizes: [],
    logo: "/hackathon/sponsors/pioneer.svg",
    logoWidth: 112,
    logoHeight: 28,
  },
  {
    kicker: "Band",
    title: "Best Use of Band",
    prizes: ["$500"],
    criteria:
      "Coordination between your agents has to happen inside Band, so that taking Band out breaks the project. Teams building with Jam should show the Band chatroom in their video and let the agents collaborate on camera.",
    logo: "/hackathon/sponsors/band.svg",
    logoWidth: 533,
    logoHeight: 144,
  },
  {
    kicker: "Render",
    title: "Best Use of Render",
    prizes: ["$500", "$300", "$100"],
    criteria:
      "Paid in Render credits. Projects have to use Render Workflows to qualify.",
    logo: "/hackathon/sponsors/render.svg",
    logoWidth: 110,
    logoHeight: 21,
  },
];

export interface Person {
  name: string;
  role: string;
  bio: string;
  photo?: string;
}

export const JUDGES: Person[] = [
  {
    name: "Ilia Bolgov",
    role: "Co-Founder & CEO, Touchmark (YC S26)",
    bio: "Product Owner at Revolut. Maths and Finance at Imperial College London.",
    photo: "/events/judges/ilia-bolgov.jpg",
  },
  {
    name: "Roman Yanushevskyi",
    role: "Co-Founder & CTO, Touchmark (YC S26)",
    bio: "AI Engineer at Lovable. Quant at Citadel Securities.",
    photo: "/events/judges/roman-yanushevskyi.jpg",
  },
  {
    name: "Om Buddhdev",
    role: "Co-Founder & CEO, Olam Labs (YC S26)",
    bio: "Engineer at Latitude. Global top-25 Valorant player with 200k+ followers.",
    photo: "/events/judges/om-buddhdev.jpg",
  },
  {
    name: "Shreshth Sharma",
    role: "Co-Founder & CTO, Olam Labs (YC S26)",
    bio: "Data Scientist at RBC. Math, CS, and Business at Waterloo.",
    photo: "/events/judges/shreshth-sharma.jpg",
  },
  {
    name: "Shubh Mittal",
    role: "Founder, Stealth",
    bio: "MTS at xAI. Engineer at AGI Inc.",
    photo: "/events/judges/shubh-mittal.jpg",
  },
  {
    name: "Sarthak Ahuja",
    role: "Co-Founder, Brekfuz",
    bio: "Founder at GR8ER. Z Fellow, CS at UIUC.",
    photo: "/events/judges/sarthak-ahuja.jpg",
  },
  {
    name: "Arhan Singhal",
    role: "Co-Founder, Brekfuz",
    bio: "Founder at SecureSense. Z Fellow, AI at UPenn.",
    photo: "/events/judges/arhan-singhal.jpg",
  },
];

export const SPEAKERS: Person[] = [
  {
    name: "Ari Dutilh",
    role: "Media & Talent, Solo Founders and ODF",
    bio: "Co-Founder at Bagel Fund. Founder at Photography Lounge.",
    photo: "/events/speakers/ari-dutilh.jpg",
  },
  {
    name: "Danielle Strachman",
    role: "Founder & General Partner, 1517 Fund",
    bio: "Director of the Thiel Fellowship. Founder of Innovations Academy.",
    photo: "/events/speakers/danielle-strachman.jpg",
  },
];

export interface Criterion {
  weight: string;
  title: string;
  body: string;
}

export const JUDGING: Criterion[] = [
  {
    weight: "40",
    title: "Project improvement",
    body: "A real, credible improvement driven by the human input, ideally a before and after human eval run on Terac.",
  },
  {
    weight: "35",
    title: "What you built",
    body: "The app or environment itself: task design, creativity, and UX.",
  },
  {
    weight: "25",
    title: "Use of human input",
    body: "Quality of the input, and getting signal efficiently within your credit.",
  },
];

export interface Perk {
  sponsor: string;
  body: string;
}

// Redemption links and codes stay off the public page: several of the offers are
// capped or single-use. Attendees get them in the doc handed out at the opening
// ceremony.
export const PERKS: Perk[] = [
  {
    sponsor: "Stripe",
    body: "20% off Stripe Atlas. Incorporate with Atlas and you also get $2,500 in Stripe credits, and can create, send, and receive SAFEs straight from your Stripe dashboard.",
  },
  {
    sponsor: "Render",
    body: "Platform credits for anything your workspace accrues: the plan fee, compute, bandwidth, and the rest.",
  },
  {
    sponsor: "Replay",
    body: "Unlimited access to the QA platform for the duration of the hackathon.",
  },
  {
    sponsor: "Band",
    body: "A free month of the Pro tier, plus the Band Discord and hacker guide if you get stuck.",
  },
  {
    sponsor: "Interview Cake",
    body: "Seven days of the full coding-interview prep course, a $50 value. First 50 redemptions, expires September 30, 2026.",
  },
];

export interface Faq {
  q: string;
  a: string;
}

export const FAQ: Faq[] = [
  {
    q: "Who can enter?",
    a: "Anyone building with AI. Bring a team, or find one at kickoff. You will sign in to Terac as a researcher for the day.",
  },
  {
    q: "Can I enter more than one track?",
    a: "Yes. Enter as many tracks as your project qualifies for. The more tracks you enter, the better your odds of winning one.",
  },
  {
    q: "Do I need to train a model to win?",
    a: "No. What matters is that real human judgment changed your project for the better, and you can show it.",
  },
  {
    q: "What is the Terac API?",
    a: "Terac makes human labor available on demand. You define the task and the kind of person you need; Terac handles recruitment, screening, verification, and payouts.",
  },
  {
    q: "How do teams get credits?",
    a: "API keys and credits go out in the attendee doc shared at the opening ceremony. Sponsors take credit and API questions at their booths from 10:20.",
  },
  {
    q: "Who will I reach?",
    a: "General-population participants, so turnaround stays fast and your credit goes further. No specialist panels are needed to win.",
  },
  {
    q: "What do I need by the end?",
    a: "A working app, real human input collected through Terac, and a measurable before and after. Submissions lock at 18:45.",
  },
  {
    q: "Where do I host my project?",
    a: "Anywhere you like, but the Best Use of Render track only considers projects that use Render Workflows.",
  },
  {
    q: "What should I bring?",
    a: "A laptop, your chargers, and something you want real people to react to. We handle food and the people.",
  },
];
